import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  mockNewAffiliateCommissionsByProduct,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportNewAffiliateCommissionsByProductData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { toast } from 'react-toastify';
import { useProducts } from '@src/hooks/useProducts';
import { useDisplayedProductsForTableAndChart } from '@src/hooks/useDisplayedProductsForTableAndChart';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const NewAffiliateCommissionsByProduct: FC = () => {
  // Data state
  const [data, setData] = useState<
    TReportNewAffiliateCommissionsByProductData[]
  >([]);
  const { products } = useProducts();

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.THIS_YEAR,
    defaultGroupBy: ReportGroupBy.MONTH,
    enableComparisons: true,
  });

  const { selectedItemsForDisplay } = useDisplayedProductsForTableAndChart(
    products,
    filters.selectedProducts
  );

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: selectedItemsForDisplay,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products?.[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
    itemsField: 'products',
    includeTotalsRow: true,
    valueFormatter: (_key: string, value: number) =>
      `$${value.toLocaleString()}`,
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products?.[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      // TODO: Replace with actual API call when available
      // const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      // const response = await getNewAffiliateCommissionsByProduct(payload);
      // setData(response.data);

      // Using mock data for now
      setData(mockNewAffiliateCommissionsByProduct);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      // TODO: Implement export when API is available
      // const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      // const response = await exportNewAffiliateCommissionsByProduct(payload);
      // downloadFile(response.data, 'new-affiliate-commissions-by-product');
      toast.info('Export feature will be available when API is ready');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        New Affiliate Commissions by Product
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showProductsFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      {data.length > 0 && (
        <>
          <Card className="p-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white">
                Commission Trends
              </h2>
              <ResponsiveContainer height={400} width="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    stroke="#999"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis
                    stroke="#999"
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    iconType="line"
                    wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                  />

                  {/* Main data lines */}
                  {displayItems.map((product, index) => (
                    <Line
                      key={product}
                      activeDot={{ r: 6 }}
                      dataKey={product}
                      dot={{ fill: getChartColor(index), r: 4 }}
                      name={product}
                      stroke={getChartColor(index)}
                      strokeWidth={2}
                      type="monotone"
                    />
                  ))}

                  {/* Previous period lines */}
                  {filters.compareToPreviousPeriod &&
                    displayItems.map((product, index) => (
                      <Line
                        key={`${product}_previous_period`}
                        activeDot={{ r: 4 }}
                        dataKey={`${product}_previous_period`}
                        dot={{
                          fill: getChartColor(index),
                          r: 3,
                        }}
                        name={`${product} (Previous Period)`}
                        stroke={getChartColor(index)}
                        strokeDasharray="5 5"
                        strokeWidth={1.5}
                        type="monotone"
                      />
                    ))}

                  {/* Previous year lines */}
                  {filters.compareToSamePeriodPreviousYear &&
                    displayItems.map((product, index) => (
                      <Line
                        key={`${product}_previous_year`}
                        activeDot={{ r: 4 }}
                        dataKey={`${product}_previous_year`}
                        dot={{
                          fill: getChartColor(index),
                          r: 3,
                        }}
                        name={`${product} (Previous Year)`}
                        stroke={getChartColor(index)}
                        strokeDasharray="2 2"
                        strokeWidth={1.5}
                        type="monotone"
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white">
                Detailed Data
              </h2>
              <RadixTable columns={columns} data={tableData} />
              <Divider className="my-4" />
              <div className="flex justify-end">
                <Button
                  className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                  variant="outline"
                  onClick={handleExportReport}
                >
                  Download CSV
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};
